import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Building, MapPin, Edit, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { ClimateFacility } from '@/types/climate';
import { useToast } from '@/hooks/use-toast';

export const FacilityManager = () => {
  const [facilities, setFacilities] = useState<ClimateFacility[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFacility, setEditingFacility] = useState<ClimateFacility | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [facilityToDelete, setFacilityToDelete] = useState<ClimateFacility | null>(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState<{
    facility_name: string;
    facility_type: ClimateFacility['facility_type'];
    location_address: string;
    latitude: string;
    longitude: string;
    country: string;
    region: string;
    operational_status: ClimateFacility['operational_status'];
    facility_description: string;
    annual_revenue: string;
    employee_count: string;
    energy_consumption: string;
    water_consumption: string;
  }>({
    facility_name: '',
    facility_type: 'office',
    location_address: '',
    latitude: '',
    longitude: '',
    country: '',
    region: '',
    operational_status: 'active',
    facility_description: '',
    annual_revenue: '',
    employee_count: '',
    energy_consumption: '',
    water_consumption: ''
  });

  useEffect(() => {
    loadFacilities();
  }, []);

  const loadFacilities = async () => {
    try {
      const { data, error } = await supabase
        .from('climate_facilities' as any)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setFacilities((data as unknown as ClimateFacility[]) || []);
    } catch (error) {
      console.error('Error loading facilities:', error);
      toast({
        title: "Error",
        description: "Failed to load facilities",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      const facilityData = {
        ...formData,
        user_id: user.id,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        annual_revenue: formData.annual_revenue ? parseFloat(formData.annual_revenue) : null,
        employee_count: formData.employee_count ? parseInt(formData.employee_count) : null,
        energy_consumption: formData.energy_consumption ? parseFloat(formData.energy_consumption) : null,
        water_consumption: formData.water_consumption ? parseFloat(formData.water_consumption) : null,
      };

      if (editingFacility) {
        const { error } = await supabase
          .from('climate_facilities' as any)
          .update(facilityData)
          .eq('id', editingFacility.id);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Facility updated successfully",
        });
      } else {
        const { error } = await supabase
          .from('climate_facilities' as any)
          .insert([facilityData]);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Facility created successfully",
        });
      }

      resetForm();
      setIsModalOpen(false);
      loadFacilities();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to save facility",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (facility: ClimateFacility) => {
    setEditingFacility(facility);
    setFormData({
      facility_name: facility.facility_name,
      facility_type: facility.facility_type,
      location_address: facility.location_address || '',
      latitude: facility.latitude?.toString() || '',
      longitude: facility.longitude?.toString() || '',
      country: facility.country || '',
      region: facility.region || '',
      operational_status: facility.operational_status,
      facility_description: facility.facility_description || '',
      annual_revenue: facility.annual_revenue?.toString() || '',
      employee_count: facility.employee_count?.toString() || '',
      energy_consumption: facility.energy_consumption?.toString() || '',
      water_consumption: facility.water_consumption?.toString() || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = (facility: ClimateFacility) => {
    setFacilityToDelete(facility);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!facilityToDelete) return;

    try {
      const { error } = await supabase
        .from('climate_facilities' as any)
        .delete()
        .eq('id', facilityToDelete.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Facility deleted successfully",
      });

      setIsDeleteDialogOpen(false);
      setFacilityToDelete(null);
      loadFacilities();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete facility",
        variant: "destructive",
      });
      setIsDeleteDialogOpen(false);
      setFacilityToDelete(null);
    }
  };

  const resetForm = () => {
    setFormData({
      facility_name: '',
      facility_type: 'office',
      location_address: '',
      latitude: '',
      longitude: '',
      country: '',
      region: '',
      operational_status: 'active',
      facility_description: '',
      annual_revenue: '',
      employee_count: '',
      energy_consumption: '',
      water_consumption: ''
    });
    setEditingFacility(null);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading facilities...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Facility Management</h2>
          <p className="text-muted-foreground">Manage your facilities for climate risk assessment</p>
        </div>

        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm}>
              <Plus className="h-4 w-4 mr-2" />
              Add Facility
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingFacility ? 'Edit Facility' : 'Add New Facility'}</DialogTitle>
              <DialogDescription>
                Enter facility details for climate risk assessment
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="facility_name">Facility Name *</Label>
                  <Input
                    id="facility_name"
                    value={formData.facility_name}
                    onChange={(e) => setFormData({ ...formData, facility_name: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="facility_type">Facility Type *</Label>
                  <Select value={formData.facility_type} onValueChange={(value: ClimateFacility['facility_type']) => setFormData({ ...formData, facility_type: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manufacturing">Manufacturing</SelectItem>
                      <SelectItem value="office">Office</SelectItem>
                      <SelectItem value="warehouse">Warehouse</SelectItem>
                      <SelectItem value="data_center">Data Center</SelectItem>
                      <SelectItem value="retail">Retail</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="location_address">Location Address</Label>
                  <Input
                    id="location_address"
                    value={formData.location_address}
                    onChange={(e) => setFormData({ ...formData, location_address: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="latitude">Latitude</Label>
                  <Input
                    id="latitude"
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="longitude">Longitude</Label>
                  <Input
                    id="longitude"
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="region">Region</Label>
                  <Input
                    id="region"
                    value={formData.region}
                    onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="operational_status">Operational Status</Label>
                  <Select value={formData.operational_status} onValueChange={(value: ClimateFacility['operational_status']) => setFormData({ ...formData, operational_status: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="planned">Planned</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="employee_count">Employee Count</Label>
                  <Input
                    id="employee_count"
                    type="number"
                    value={formData.employee_count}
                    onChange={(e) => setFormData({ ...formData, employee_count: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="annual_revenue">Annual Revenue ($)</Label>
                  <Input
                    id="annual_revenue"
                    type="number"
                    step="0.01"
                    value={formData.annual_revenue}
                    onChange={(e) => setFormData({ ...formData, annual_revenue: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="energy_consumption">Energy Consumption (kWh/year)</Label>
                  <Input
                    id="energy_consumption"
                    type="number"
                    step="0.01"
                    value={formData.energy_consumption}
                    onChange={(e) => setFormData({ ...formData, energy_consumption: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="water_consumption">Water Consumption (m³/year)</Label>
                  <Input
                    id="water_consumption"
                    type="number"
                    step="0.01"
                    value={formData.water_consumption}
                    onChange={(e) => setFormData({ ...formData, water_consumption: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="facility_description">Description</Label>
                <Textarea
                  id="facility_description"
                  value={formData.facility_description}
                  onChange={(e) => setFormData({ ...formData, facility_description: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingFacility ? 'Update Facility' : 'Create Facility'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {facilities.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Building className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Facilities Found</h3>
              <p className="text-muted-foreground text-center mb-4">
                Start by adding your first facility to begin climate risk assessment
              </p>
              <Button onClick={() => setIsModalOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Facility
              </Button>
            </CardContent>
          </Card>
        ) : (
          facilities.map((facility) => (
            <Card key={facility.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Building className="h-5 w-5" />
                      {facility.facility_name}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-1">
                      <MapPin className="h-4 w-4" />
                      {facility.location_address || 'No address specified'}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(facility)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(facility)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm font-medium">Type</p>
                    <Badge variant="secondary">
                      {facility.facility_type.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Status</p>
                    <Badge variant={facility.operational_status === 'active' ? 'default' : 'secondary'}>
                      {facility.operational_status.toUpperCase()}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Employees</p>
                    <p className="text-sm text-muted-foreground">
                      {facility.employee_count || 'Not specified'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Location</p>
                    <p className="text-sm text-muted-foreground">
                      {facility.country || 'Not specified'}
                    </p>
                  </div>
                </div>
                {facility.facility_description && (
                  <div className="mt-4">
                    <p className="text-sm font-medium">Description</p>
                    <p className="text-sm text-muted-foreground">{facility.facility_description}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{facilityToDelete?.facility_name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setFacilityToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
